import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Send, FlaskConical, Loader2, BarChart3, Download, Copy, Lock, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TargetPicker } from "@/components/admin/StudioMailTab";

const QTYPES = [["single", "Choix unique"], ["multi", "Choix multiples"], ["rating", "Note 1→5 ★"], ["yesno", "Oui / Non"], ["text", "Texte libre"]];
const newQ = () => ({ id: Math.random().toString(36).slice(2, 10), type: "single", label: "", options: ["", ""], required: true });
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d[0]?.msg : "Erreur"; };

export default function FormsTab() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState([newQ()]);
  const [target, setTarget] = useState({ type: "clients", emails: "", zone_code: "", zone_role: "all" });
  const [channels, setChannels] = useState({ send_push: true, send_email: true });
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forms, setForms] = useState([]);
  const [resultsId, setResultsId] = useState(null);

  const load = useCallback(() => api.get("/admin/price/forms").then((r) => setForms(r.data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setCount(null);
    if (target.type === "zone" && !target.zone_code) return undefined;
    if (target.type === "list" && !target.emails.trim()) { setCount(0); return undefined; }
    const t = setTimeout(() => api.post("/admin/price/mail/count", target).then((r) => setCount(r.data.count)).catch(() => setCount(0)), 500);
    return () => clearTimeout(t);
  }, [target]);

  const validate = () => {
    if (title.trim().length < 2) return "Ajoute un titre (2 caractères min.)";
    for (const [i, q] of questions.entries()) {
      if (!q.label.trim()) return `Question ${i + 1} : écris la question`;
      if ((q.type === "single" || q.type === "multi") && new Set(q.options.map((o) => o.trim()).filter(Boolean)).size < 2) return `Question ${i + 1} : ajoute au moins 2 choix différents`;
    }
    if (!channels.send_push && !channels.send_email) return "Choisis au moins un canal (app ou e-mail)";
    return null;
  };

  const send = async (test) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!test && !window.confirm(`Envoyer le questionnaire à ${count} personne(s) ?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/price/forms${test ? "?test=true" : ""}`, { title, description, questions, target: test ? null : target, ...channels });
      toast.success(test ? "Test envoyé (app + contact@pricecovoit.fr)" : "Questionnaire envoyé");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const duplicate = (f) => {
    setTitle(f.title.replace(/^\[TEST\] /, "")); setDescription(f.description || "");
    setQuestions(f.questions.map((q) => ({ ...q, options: q.options?.length ? q.options : ["", ""] })));
    if (f.target?.type && f.target.type !== "test") setTarget((t) => ({ ...t, ...f.target, emails: f.target.emails || "" }));
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast("Questionnaire chargé — modifie puis renvoie");
  };

  const upd = (i, p) => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...p } : q)));
  const move = (i, d) => setQuestions((qs) => { const n = [...qs]; const j = i + d; if (j < 0 || j >= n.length) return qs; [n[i], n[j]] = [n[j], n[i]]; return n; });

  return (
    <div className="space-y-4" data-testid="forms-tab">
      <div className="space-y-2 rounded-2xl bg-white p-3 street-border-thin">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre du questionnaire" maxLength={150} className="w-full rounded-xl bg-secondary/30 px-3 py-2.5 text-[15px] font-black street-border-thin" data-testid="form-title" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Petit texte d'intro (optionnel)" rows={2} maxLength={1000} className="w-full rounded-xl bg-secondary/30 px-3 py-2 text-[13px] street-border-thin" data-testid="form-description" />
      </div>
      {questions.map((q, i) => (
        <QuestionEditor key={q.id} q={q} i={i} last={i === questions.length - 1} onChange={(p) => upd(i, p)} onMove={(d) => move(i, d)}
          onDelete={() => setQuestions((qs) => (qs.length > 1 ? qs.filter((_, j) => j !== i) : qs))} />
      ))}
      <button onClick={() => setQuestions((qs) => [...qs, newQ()])} className="street-press inline-flex items-center gap-1 rounded-xl bg-secondary/50 px-3 py-2 text-[12px] font-black street-border-thin" data-testid="form-add-question">
        <Plus className="h-4 w-4" /> Ajouter une question
      </button>
      <TargetPicker target={target} setTarget={setTarget} count={count} />
      <div className="flex flex-wrap gap-4 rounded-2xl bg-white p-3 text-[13px] font-bold street-border-thin">
        <label className="flex items-center gap-2"><input type="checkbox" checked={channels.send_push} onChange={(e) => setChannels((c) => ({ ...c, send_push: e.target.checked }))} data-testid="form-channel-push" /> Notif + fenêtre dans l&apos;app</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={channels.send_email} onChange={(e) => setChannels((c) => ({ ...c, send_email: e.target.checked }))} data-testid="form-channel-email" /> E-mail avec lien</label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => send(true)} disabled={busy} className="street-press inline-flex items-center gap-1.5 rounded-xl bg-white px-4 py-2.5 text-[13px] font-black street-border-thin" data-testid="form-send-test"><FlaskConical className="h-4 w-4" /> M&apos;envoyer un test</button>
        <button onClick={() => send(false)} disabled={busy || !count} className="street-press inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-50" data-testid="form-send">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer{count ? ` à ${count}` : ""}
        </button>
      </div>
      <FormsList items={forms} onResults={setResultsId} onDuplicate={duplicate} onChanged={load} />
      {resultsId && <ResultsModal id={resultsId} onClose={() => setResultsId(null)} />}
    </div>
  );
}

function QuestionEditor({ q, i, last, onChange, onMove, onDelete }) {
  const hasOpts = q.type === "single" || q.type === "multi";
  return (
    <div className="space-y-2 rounded-2xl bg-white p-3 street-border-thin" data-testid={`form-question-${i}`}>
      <div className="flex items-center gap-1">
        <span className="flex-1 text-[11px] font-black uppercase tracking-widest text-foreground/50">Question {i + 1}</span>
        <button onClick={() => onMove(-1)} disabled={i === 0} aria-label="Monter" data-testid={`form-question-up-${i}`} className="rounded p-1 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
        <button onClick={() => onMove(1)} disabled={last} aria-label="Descendre" data-testid={`form-question-down-${i}`} className="rounded p-1 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
        <button onClick={onDelete} className="rounded p-1 text-red-600" data-testid={`form-question-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <input value={q.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Ta question" maxLength={300} className="w-full rounded-lg bg-secondary/30 px-2.5 py-2 text-[14px] font-bold street-border-thin" data-testid={`form-question-label-${i}`} />
      <div className="flex flex-wrap items-center gap-2">
        <select value={q.type} onChange={(e) => onChange({ type: e.target.value })} className="rounded-lg px-2 py-1.5 text-[12px] font-bold street-border-thin" data-testid={`form-question-type-${i}`}>
          {QTYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-[12px] font-bold"><input type="checkbox" checked={q.required} onChange={(e) => onChange({ required: e.target.checked })} data-testid={`form-question-required-${i}`} /> Obligatoire</label>
      </div>
      {hasOpts && (
        <div className="space-y-1.5">
          {q.options.map((o, j) => (
            <div key={j} className="flex gap-1.5">
              <input value={o} onChange={(e) => onChange({ options: q.options.map((x, k) => (k === j ? e.target.value : x)) })} placeholder={`Choix ${j + 1}`} maxLength={120}
                className="flex-1 rounded-lg bg-secondary/30 px-2.5 py-1.5 text-[13px] street-border-thin" data-testid={`form-question-${i}-opt-${j}`} />
              <button onClick={() => onChange({ options: q.options.filter((_, k) => k !== j) })} disabled={q.options.length <= 2} aria-label="Retirer ce choix" data-testid={`form-question-${i}-remove-opt-${j}`} className="rounded p-1 text-red-600 disabled:opacity-30"><X className="h-4 w-4" /></button>
            </div>
          ))}
          {q.options.length < 12 && <button onClick={() => onChange({ options: [...q.options, ""] })} className="text-[12px] font-black text-primary" data-testid={`form-question-${i}-add-opt`}>+ Ajouter un choix</button>}
        </div>
      )}
    </div>
  );
}

function FormsList({ items, onResults, onDuplicate, onChanged }) {
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } onChanged(); };
  const close = (f) => window.confirm("Clôturer ce questionnaire ?") && run(() => api.post(`/admin/price/forms/${f.id}/close`), "Questionnaire clôturé");
  const del = (f) => window.confirm("Supprimer ce questionnaire et ses réponses ?") && run(() => api.delete(`/admin/price/forms/${f.id}`), "Questionnaire supprimé");
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid="forms-list">
      <div className="mb-2 font-[Outfit] text-[15px] font-black">Mes questionnaires</div>
      {!items.length && <div className="text-[13px] text-foreground/50">Aucun questionnaire envoyé.</div>}
      <div className="divide-y divide-black/5">
        {items.map((f) => (
          <div key={f.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`forms-row-${f.id}`}>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-black">{f.title}</div>
              <div className="text-[11px] text-foreground/60">
                {new Date(f.created_at).toLocaleDateString("fr-FR")} · {f.responses_count}/{f.recipients_count} réponses · {f.status === "open" ? "Ouvert" : "Clôturé"}
              </div>
            </div>
            <button onClick={() => onResults(f.id)} className="inline-flex items-center gap-1 rounded-lg bg-primary px-2 py-1.5 text-[11px] font-black text-primary-foreground street-border-thin" data-testid={`forms-results-${f.id}`}><BarChart3 className="h-3 w-3" />Résultats</button>
            <button onClick={() => onDuplicate(f)} className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1.5 text-[11px] font-black street-border-thin" data-testid={`forms-duplicate-${f.id}`}><Copy className="h-3 w-3" />Réutiliser</button>
            {f.status === "open" && <button onClick={() => close(f)} className="rounded-lg p-1.5" title="Clôturer" data-testid={`forms-close-${f.id}`}><Lock className="h-4 w-4" /></button>}
            <button onClick={() => del(f)} className="rounded-lg p-1.5 text-red-600" data-testid={`forms-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultsModal({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/price/forms/${id}/results`).then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible")); }, [id]);
  const csv = async () => {
    try {
      const r = await api.get(`/admin/price/forms/${id}/csv`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `questionnaire-${id.slice(0, 8)}.csv`; a.click();
    } catch { toast.error("Export impossible"); }
  };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" data-testid="forms-results-modal" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-[#FFF8F1] p-5 street-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2">
          <div className="font-[Outfit] text-[20px] font-black">{data?.form?.title || "…"}</div>
          <div className="flex gap-2">
            <button onClick={csv} className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-[12px] font-black street-border-thin" data-testid="forms-csv"><Download className="h-4 w-4" />CSV</button>
            <button onClick={onClose} className="rounded-lg bg-white p-1.5 street-border-thin" data-testid="forms-results-close"><X className="h-4 w-4" /></button>
          </div>
        </div>
        {!data && <Loader2 className="mx-auto mt-8 h-6 w-6 animate-spin text-primary" />}
        {data && <div className="mt-1 text-[12px] font-bold text-foreground/60">{data.responses.length} réponse(s) sur {data.form.recipients_count}</div>}
        {data?.stats.map((s) => <StatBlock key={s.id} s={s} />)}
        {data?.responses.length > 0 && (
          <details className="mt-4 rounded-2xl bg-white p-3 street-border-thin">
            <summary className="cursor-pointer text-[13px] font-black">Réponses par personne</summary>
            {data.responses.map((r, i) => (
              <div key={i} className="border-t border-black/5 py-2 text-[12px]">
                <div className="font-black">{r.name || r.email} <span className="font-normal text-foreground/50">· {new Date(r.created_at).toLocaleString("fr-FR")}</span></div>
                {data.form.questions.map((q) => <div key={q.id}><span className="text-foreground/60">{q.label} :</span> {r.answers[q.id] || "—"}</div>)}
              </div>
            ))}
          </details>
        )}
      </div>
    </div>
  );
}

function StatBlock({ s }) {
  const max = Math.max(1, ...Object.values(s.counts || {}));
  return (
    <div className="mt-3 rounded-2xl bg-white p-3 street-border-thin" data-testid={`forms-stat-${s.id}`}>
      <div className="text-[14px] font-black">{s.label}</div>
      <div className="text-[11px] text-foreground/50">{s.answered} réponse(s)</div>
      {s.type === "rating" && s.average != null && (
        <div className="mt-1 flex items-center gap-1 font-[Outfit] text-[22px] font-black"><Star className="h-5 w-5 text-[#FFB800]" fill="currentColor" />{s.average}/5</div>
      )}
      {s.counts && (
        <div className="mt-2 space-y-1">
          {Object.entries(s.counts).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-[12px]">
              <span className="w-28 flex-none truncate font-bold">{s.type === "rating" ? `${k} ★` : k}</span>
              <div className="h-4 flex-1 overflow-hidden rounded bg-secondary/40"><div className="h-full bg-primary" style={{ width: `${(v / max) * 100}%` }} /></div>
              <span className="w-16 text-right font-black">{v} · {s.answered ? Math.round((v / s.answered) * 100) : 0}%</span>
            </div>
          ))}
        </div>
      )}
      {s.texts && (
        <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
          {s.texts.map((t, i) => <div key={i} className="rounded-lg bg-secondary/30 px-2 py-1 text-[12px]"><b>{t.name}</b> : {t.text}</div>)}
          {!s.texts.length && <div className="text-[12px] text-foreground/50">Aucune réponse texte.</div>}
        </div>
      )}
    </div>
  );
}
